// TeamPeek — the team peek body, rendered by the global PeekHost from
// `?peek=teams:<id>` (ADR-036 §2). Fetches the team DETAIL (PeekHost is global;
// it cannot rely on a list being loaded) and renders the peek grammar (ADR-036
// §7): boxed dense sections mirroring the page (the members — the page's ONE
// list in its dense face, the count in the section's slot — where it works, the
// chains read-only, the clock), and the ACTS composed by the panel
// from the team's ONE registry (`useTeamActions`) — the top row = Add members
// (the primary, a quiet button) + the Edit menu (Edit team · Manage
// responsibilities · Edit work area), the footer = the labelled Actions door
// (Disable / Reactivate · Delete) + "Open full page". The overlays the acts
// open are mounted ONCE, beside the panel. A person acts from here; the page
// reads everything.

import { useTranslation } from 'react-i18next';
import type { TeamDetail } from '@ems-app/schemas/teams/responses';
import { useTeam } from '../../hooks/useTeams.js';
import { ContextPanel } from '../../components/patterns/ContextPanel.js';
import { DetailSection } from '../../components/patterns/DetailSection.js';
import { DescriptionList, Field } from '../../components/patterns/DescriptionList.js';
import { Alert } from '../../components/ui/Alert.js';
import { PageLoader } from '../../components/ui/PageLoader.js';
import { icons } from '../../components/ui/icons.js';
import { TeamStatusBadge, teamZoneWord } from './teamDisplay.js';
import { useTeamActions } from './TeamActions.js';
import { TeamMembersSection } from './TeamMembersSection.js';
import { TeamWorkAreaSection } from './TeamWorkAreaSection.js';
import { ScopeChainsSection } from '../../components/patterns/ScopeChainsSection.js';
import { useEntityPeek } from '../../hooks/useEntityPeek.js';
import { formatNumber } from '../../lib/format.js';

export function TeamPeek({ projectId, teamId, onClose }: { projectId: string; teamId: string; onClose: () => void }) {
  const { t } = useTranslation('teams');
  const team = useTeam(projectId, teamId);

  if (team.isPending) {
    return (
      <ContextPanel host="peek" onClose={onClose} title={t('title')}>
        <div className="flex min-h-[8rem] items-center justify-center">
          <PageLoader />
        </div>
      </ContextPanel>
    );
  }
  if (team.isError || !team.data) {
    return (
      <ContextPanel host="peek" onClose={onClose} title={t('title')}>
        <Alert variant="danger">{t('detail.error')}</Alert>
      </ContextPanel>
    );
  }
  return <TeamPeekBody projectId={projectId} team={team.data} onClose={onClose} />;
}

// The loaded peek — its own component so the acts hook runs on a team that is there.
function TeamPeekBody({ projectId, team: td, onClose }: { projectId: string; team: TeamDetail; onClose: () => void }) {
  const { t } = useTranslation('teams');
  const { acts, overlays } = useTeamActions(projectId, td, { onDeleted: onClose });
  // A member — on the list or a chain — is followed inside the sheet: the team joins the way back.
  const { followPeek } = useEntityPeek();
  const peekPerson = (userId: string) => followPeek(`users:${userId}`, td.name);
  return (
    <>
      <ContextPanel
        host="peek"
        onClose={onClose}
        leading={<icons.team size={24} aria-hidden className="text-[var(--color-text-secondary)]" />}
        kind={t('kind')}
        title={td.name}
        status={<TeamStatusBadge disabled={td.is_disabled} />}
        openFullPath={`/p/${projectId}/teams/${td.id}`}
        acts={acts}
      >
        {/* The page's one list of people in its dense face; the count in the section's slot, the bare number — the title names it (ADR-036 §7; no act — the primary's one home is the top row). */}
        <DetailSection
          dense
          title={t('detail.sections.members')}
          action={td.members.length > 0 ? <span className="text-sm text-[var(--color-text-secondary)] tabular-nums">{formatNumber(td.members.length)}</span> : undefined}
        >
          <TeamMembersSection projectId={projectId} team={td} canManage={false} dense peekPerson={peekPerson} />
        </DetailSection>
        <DetailSection dense title={t('detail.sections.responsibilities')}>
          <ScopeChainsSection projectId={projectId} scope={{ level: 'team', id: td.id }} peekPerson={peekPerson} />
        </DetailSection>
        <DetailSection dense title={t('detail.sections.worksIn')}>
          <TeamWorkAreaSection area={td.work_area} />
        </DetailSection>
        <DetailSection dense title={t('detail.sections.record')}>
          <DescriptionList>
            <Field label={t('detail.timezone')}>{teamZoneWord(t, td.timezone_iana)}</Field>
          </DescriptionList>
        </DetailSection>
      </ContextPanel>
      {overlays}
    </>
  );
}
